import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FaPlane } from "react-icons/fa";
import { useAppContext } from "../../../contexts/AppContext";
import { useSearch } from "../../../contexts/SearchContext";
import { adaptDealToFlightOffer } from "../adapters/flightOfferAdapter";
import { useBookingFlow } from "./hooks/useBookingFlow";
import FlightBuilderSummaryCard from "./selfCompose/FlightBuilderSummaryCard";
import { dealKey } from "./selfCompose/selfComposeSummaryUtils";
import { useSelfComposeUi } from "./selfCompose/SelfComposeUiContext";
import { cheapestOfferIdsByPerPerson } from "./cheapestOfferIds";
import FlightOfferResultCardLayout from "./FlightOfferResultCardLayout";
import FlightResultsFiltersPanel from "./FlightResultsFiltersPanel";
import FlightResultsLoadingSkeleton from "./FlightResultsLoadingSkeleton";
import {
  extractFlightOfferTraits,
  defaultFlightResultsFilterState,
  flightOfferMatchesFilters,
  sortFlightOffers,
  computePriceAndDurationBounds,
} from "./flightResultsFilterUtils";
import "./selfCompose/SelfComposeFlow.css";
import { shouldIgnoreNavigationClick } from "../../../utils/interaction/shouldIgnoreNavigationClick";
import { scrollSearchOfferFlightFlowToTop } from "../flightBookingPathUtils";

const EMPTY_ROUTE_COUNTS = { rt: 0, owOut: 0, owIn: 0, total: 0 };

function SelfComposeOfferResultsList({
  offers,
  selectedDeal,
  onPick,
  ctaLabel,
  loading,
  emptyTitle,
  emptyText,
}) {
  const { isMobile, airlines } = useAppContext();
  const dragRef = useRef({
    isPointerDown: false,
    startX: 0,
    startY: 0,
    dragged: false,
  });
  const DRAG_THRESHOLD_PX = 5;

  const [filterState, setFilterState] = useState(defaultFlightResultsFilterState);
  const [sortMode, setSortMode] = useState("cheapest");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const searchFingerprint = useMemo(
    () =>
      `${offers.length}_${offers[0]?.id ?? ""}_${offers[offers.length - 1]?.id ?? ""}`,
    [offers]
  );

  useEffect(() => {
    setFilterState(defaultFlightResultsFilterState());
    setSortMode("cheapest");
    setDrawerOpen(false);
  }, [searchFingerprint]);

  const setFilter = useCallback((patch) => {
    setFilterState((prev) => ({ ...prev, ...patch }));
  }, []);

  const onClearFilters = useCallback(() => {
    setFilterState(defaultFlightResultsFilterState());
  }, []);

  const handleCardMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    dragRef.current.isPointerDown = true;
    dragRef.current.startX = e.clientX;
    dragRef.current.startY = e.clientY;
    dragRef.current.dragged = false;
  }, []);

  const handleCardMouseMove = useCallback((e) => {
    if (!dragRef.current.isPointerDown) return;
    const dx = Math.abs(e.clientX - dragRef.current.startX);
    const dy = Math.abs(e.clientY - dragRef.current.startY);
    if (dx > DRAG_THRESHOLD_PX || dy > DRAG_THRESHOLD_PX) {
      dragRef.current.dragged = true;
    }
  }, []);

  const handleCardMouseUp = useCallback(() => {
    dragRef.current.isPointerDown = false;
  }, []);

  const traitsByOfferId = useMemo(() => {
    const m = new Map();
    for (const o of offers) {
      m.set(String(o.id), extractFlightOfferTraits(o.rawDeal, o));
    }
    return m;
  }, [offers]);

  const traitsList = useMemo(
    () => Array.from(traitsByOfferId.values()),
    [traitsByOfferId]
  );

  const bounds = useMemo(
    () => computePriceAndDurationBounds(offers, traitsByOfferId),
    [offers, traitsByOfferId]
  );

  const filteredOffers = useMemo(() => {
    return offers.filter((o) => {
      const tr = traitsByOfferId.get(String(o.id));
      if (!tr) return true;
      return flightOfferMatchesFilters(tr, filterState);
    });
  }, [offers, traitsByOfferId, filterState]);

  const sortedOffers = useMemo(
    () => sortFlightOffers(filteredOffers, traitsByOfferId, sortMode),
    [filteredOffers, traitsByOfferId, sortMode]
  );

  const cheapestIds = useMemo(
    () => cheapestOfferIdsByPerPerson(sortedOffers),
    [sortedOffers]
  );

  const routeCounts = useMemo(
    () => ({ ...EMPTY_ROUTE_COUNTS, total: offers.length }),
    [offers.length]
  );

  if (loading) {
    return <FlightResultsLoadingSkeleton />;
  }

  if (offers.length === 0) {
    return (
      <div className="fsc-empty">
        <div className="fsc-empty__icon" aria-hidden>
          <FaPlane />
        </div>
        <h3 className="fsc-empty__title">{emptyTitle}</h3>
        <p className="fsc-empty__text">{emptyText}</p>
      </div>
    );
  }

  return (
    <FlightResultsFiltersPanel
      key={searchFingerprint}
      isMobile={isMobile}
      filter={filterState}
      setFilter={setFilter}
      sortMode={sortMode}
      setSortMode={setSortMode}
      bounds={bounds}
      routeCounts={routeCounts}
      showRouteFilter={false}
      traitsList={traitsList}
      drawerOpen={drawerOpen}
      setDrawerOpen={setDrawerOpen}
      numShown={sortedOffers.length}
      numTotal={offers.length}
      onClearFilters={onClearFilters}
      airlinesCatalog={airlines}
    >
      {filteredOffers.length === 0 ? (
        <p className="fw-ui-results-empty" role="status">
          אין תוצאות התואמות לסינון — נסו לרוקן חלק מהפילטרים או לשנות מיון.
        </p>
      ) : (
        <ul className="fw-ui-results-list">
          {sortedOffers.map((o, offerIndex) => {
            const selected = dealKey(selectedDeal) === o.id;
            const pick = () => onPick(o.rawDeal);
            return (
              <li key={`${String(o.id)}-${offerIndex}`}>
                <div
                  role="button"
                  tabIndex={0}
                  aria-pressed={selected}
                  aria-label={`${ctaLabel}, ${o.route ?? ""}`}
                  className={
                    selected
                      ? "fw-ui-result-row fw-ui-result-row--rich-card fw-ui-result-row--selected"
                      : "fw-ui-result-row fw-ui-result-row--rich-card"
                  }
                  onMouseDown={handleCardMouseDown}
                  onMouseMove={handleCardMouseMove}
                  onMouseUp={handleCardMouseUp}
                  onMouseLeave={handleCardMouseUp}
                  onClick={(e) => {
                    const dragged = dragRef.current.dragged;
                    dragRef.current.dragged = false;
                    if (shouldIgnoreNavigationClick(e, { dragged })) return;
                    pick();
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      pick();
                    }
                  }}
                >
                  <FlightOfferResultCardLayout
                    offer={o}
                    isCheapest={cheapestIds.has(o.id)}
                    priceColumn={null}
                    ctaMode="selfCompose"
                    primaryCtaLabel={ctaLabel}
                    selected={selected}
                    onPrimaryCtaClick={(e) => {
                      e.stopPropagation();
                      pick();
                    }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </FlightResultsFiltersPanel>
  );
}

export default function SelfComposePickStepCard() {
  const {
    outboundOneWayFlightResults,
    inboundOneWayFlightResults,
    selectedSelfComposeOutboundDeal,
    selectedSelfComposeInboundDeal,
    setSelectedSelfComposeOutboundDeal,
    setSelectedSelfComposeInboundDeal,
    roomInfo,
    loading,
    calculateLDSDealFareForRoomsComposition,
  } = useSearch();

  const { selectFlightOffer, selectedFlightOffer, searchGate } =
    useBookingFlow();

  const { buildPhase, setBuildPhase, flashMessage, setFlashMessage } =
    useSelfComposeUi();

  const phaseRootRef = useRef(null);
  const prevBuildPhaseRef = useRef(null);

  useLayoutEffect(() => {
    if (selectedSelfComposeInboundDeal && selectedSelfComposeOutboundDeal) {
      setBuildPhase("summary");
    } else if (selectedSelfComposeOutboundDeal) {
      setBuildPhase("inbound");
    } else {
      setBuildPhase("outbound");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- סנכרון ראשוני בכניסה לשלב בלבד
  }, []);

  useLayoutEffect(() => {
    const prev = prevBuildPhaseRef.current;
    prevBuildPhaseRef.current = buildPhase;
    if (prev == null || prev === buildPhase) return;

    const stepperEl =
      typeof document !== "undefined"
        ? document.querySelector(".fsc-flow-header")
        : null;
    const anchor = stepperEl || phaseRootRef.current;

    const run = () => {
      scrollSearchOfferFlightFlowToTop(anchor);
      anchor?.scrollIntoView?.({
        block: "start",
        inline: "nearest",
        behavior: "auto",
      });
    };
    run();
    const rafId = requestAnimationFrame(run);
    return () => cancelAnimationFrame(rafId);
  }, [buildPhase]);

  const outboundOffers = useMemo(() => {
    const list = outboundOneWayFlightResults || [];
    const mapped = list.map((deal, index) => {
      const priced = calculateLDSDealFareForRoomsComposition(deal);
      return adaptDealToFlightOffer(deal, index, roomInfo, { ourPrice: priced });
    });
    const sortKey = (o) => {
      const pp = Number(o.pricePerPerson);
      if (Number.isFinite(pp)) return pp;
      const usd = Number(o.priceUsd);
      if (Number.isFinite(usd)) return usd;
      return Number.POSITIVE_INFINITY;
    };
    return [...mapped].sort((a, b) => {
      const d = sortKey(a) - sortKey(b);
      if (d !== 0) return d;
      return String(a.id).localeCompare(String(b.id));
    });
  }, [outboundOneWayFlightResults, roomInfo, calculateLDSDealFareForRoomsComposition]);

  const inboundOffers = useMemo(() => {
    const list = inboundOneWayFlightResults || [];
    return list.map((deal, index) =>
      adaptDealToFlightOffer(deal, index + 1000, roomInfo, {
        ourPrice: calculateLDSDealFareForRoomsComposition(deal),
      })
    );
  }, [
    inboundOneWayFlightResults,
    roomInfo,
    calculateLDSDealFareForRoomsComposition,
  ]);

  const clearCombinedIfStale = useCallback(() => {
    if (selectedFlightOffer?.isSelfComposeCombined) {
      selectFlightOffer(null);
    }
  }, [selectFlightOffer, selectedFlightOffer?.isSelfComposeCombined]);

  const pickOutbound = useCallback(
    (rawDeal) => {
      clearCombinedIfStale();
      setSelectedSelfComposeOutboundDeal(rawDeal);
      setSelectedSelfComposeInboundDeal(null);
      setBuildPhase("inbound");
      setFlashMessage(
        "טיסת ההלוך נבחרה — עכשיו בחרו את טיסת החזור המתאימה ללקוח"
      );
    },
    [
      clearCombinedIfStale,
      setBuildPhase,
      setFlashMessage,
      setSelectedSelfComposeInboundDeal,
      setSelectedSelfComposeOutboundDeal,
    ]
  );

  const pickInbound = useCallback(
    (rawDeal) => {
      clearCombinedIfStale();
      setSelectedSelfComposeInboundDeal(rawDeal);
      setBuildPhase("summary");
    },
    [
      clearCombinedIfStale,
      setBuildPhase,
      setSelectedSelfComposeInboundDeal,
    ]
  );

  useEffect(() => {
    console.log("[FlightBookingMockup] selfComposePick", {
      roundTrip: searchGate.rtCount,
      outbound: searchGate.outboundCount,
      inbound: searchGate.inboundCount,
      buildPhase,
    });
  }, [
    buildPhase,
    searchGate.inboundCount,
    searchGate.outboundCount,
    searchGate.rtCount,
  ]);

  if (!searchGate?.needsSelfComposePickStep && !searchGate?.allowSelfComposeChoice) {
    return (
      <div className="fw-step-body">
        <p className="fw-ui-results-hint">
          שלב זה אינו זמין — אין נתונים להרכבת מסלול נפרד.
        </p>
      </div>
    );
  }

  if (searchGate.partialSelfCompose) {
    return (
      <div className="fw-step-body">
        <p className="fw-ui-results-hint fw-ui-warning-text">
          חסרה רשימת כיוונים:{" "}
          {searchGate.outboundCount === 0
            ? "אין תוצאות הלוך (יום יציאה)."
            : "אין תוצאות חזור (יום חזרה)."}
          {" "}
          לא ניתן להמשיך עד שיש שני כיוונים בתוצאות החיפוש.
        </p>
      </div>
    );
  }

  const selfComposeReady = !!(
    selectedSelfComposeOutboundDeal && selectedSelfComposeInboundDeal
  );

  return (
    <div className="fsc-root fw-step-body" ref={phaseRootRef}>
      {flashMessage ? (
        <div className="fsc-flash" role="status">
          {flashMessage}
        </div>
      ) : null}

      <div key={buildPhase} className="fsc-phase">
        {buildPhase === "outbound" && (
          <>
            <header className="fsc-phase__head">
              <h2 className="fsc-phase__title">בחר טיסת הלוך</h2>
            </header>
            <SelfComposeOfferResultsList
              offers={outboundOffers}
              selectedDeal={selectedSelfComposeOutboundDeal}
              onPick={pickOutbound}
              ctaLabel="בחר הלוך"
              loading={loading}
              emptyTitle="אין תוצאות הלוך"
              emptyText="לא נמצאו טיסות ליום היציאה. נסו לשנות תאריכים או יעד בחיפוש ולהריץ שוב."
            />
          </>
        )}

        {buildPhase === "inbound" && (
          <>
            <header className="fsc-phase__head">
              <h2 className="fsc-phase__title">בחר טיסת חזור</h2>
            </header>
            {!selectedSelfComposeOutboundDeal ? (
              <div className="fsc-empty">
                <h3 className="fsc-empty__title">קודם יש לבחור הלוך</h3>
                <p className="fsc-empty__text">
                  חזרו לשלב הלוך דרך הסטפר למעלה.
                </p>
              </div>
            ) : (
              <SelfComposeOfferResultsList
                offers={inboundOffers}
                selectedDeal={selectedSelfComposeInboundDeal}
                onPick={pickInbound}
                ctaLabel="בחר חזור"
                loading={loading}
                emptyTitle="אין תוצאות חזור"
                emptyText="לא נמצאו טיסות ליום החזרה. בדקו את החיפוש המקבילי או שנו תאריך חזרה."
              />
            )}
          </>
        )}

        {buildPhase === "summary" && (
          <>
            <header className="fsc-phase__head">
              <h2 className="fsc-phase__title">פירוט ההזמנה</h2>
            </header>
            {!selfComposeReady ? (
              <div className="fsc-empty">
                <h3 className="fsc-empty__title">חסרה בחירה</h3>
                <p className="fsc-empty__text">
                  יש לבחור הלוך וחזור לפני הסיכום. השתמשו בסטפר למעלה.
                </p>
              </div>
            ) : (
              <FlightBuilderSummaryCard
                outboundDeal={selectedSelfComposeOutboundDeal}
                inboundDeal={selectedSelfComposeInboundDeal}
                roomInfo={roomInfo}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
